import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatMoneyFromUnknown, resolveCurrencyCode } from '@/lib/money'
import * as customerService from '../../services/customerService'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { SkeletonListPage } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/ui/EmptyState'
import { LoadMoreFooter } from '../../components/ui/LoadMoreFooter'
import { useLoadMoreList } from '../../hooks/useLoadMoreList'
import { useCurrentStore } from '../../context/CurrentStoreContext'

const DEFAULT_LIMIT = 20

function formatJoinedDate(iso: string) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })
  } catch {
    return iso
  }
}

export default function CustomerList() {
  const { currentStoreId: storeId } = useCurrentStore()
  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')

  useEffect(() => {
    const t = window.setTimeout(() => setQ(searchInput.trim()), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const fetchPage = useCallback(
    async (page: number, limit: number) => {
      const res = await customerService.fetchCustomers({ page, limit, q: q || undefined })
      return { items: res.items, total: res.total }
    },
    [q],
  )

  const {
    items: customers,
    total: listTotal,
    loading,
    loadingMore,
    error: listError,
    hasMore,
    loadMore,
  } = useLoadMoreList<customerService.CustomerListItem>({
    fetchPage,
    limit: DEFAULT_LIMIT,
    resetDeps: [q, storeId],
  })

  const emptySearch = q.length > 0

  return (
    <Card>
      <CardHeader
        title="Customers"
        description="Shoppers who have placed orders at this store."
      />
      <div className="mb-4 max-w-md">
        <Input
          type="search"
          placeholder="Search by name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search customers"
        />
      </div>
      {listError ? <p className="mb-4 text-sm text-destructive">{listError}</p> : null}
      {loading ? (
        <SkeletonListPage toolbarFields={1} columns={6} rows={6} />
      ) : listTotal === 0 ? (
        <EmptyState
          title={emptySearch ? 'No customers match your search' : 'No customers yet'}
          description={
            emptySearch
              ? 'Try a different name or email.'
              : 'When shoppers register or checkout on your storefront, they will appear here.'
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/30 text-left text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">S/N</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Orders</th>
                  <th className="px-4 py-3 font-medium">Total spend</th>
                  <th className="px-4 py-3 font-medium">Joined</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {customers.map((item, rowIndex) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 tabular-nums">{rowIndex + 1}</td>
                    <td className="px-4 py-3 font-medium">{customerService.customerDisplayName(item)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{item.email || '—'}</td>
                    <td className="px-4 py-3 tabular-nums">{item.orderCount}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {formatMoneyFromUnknown(item.totalSpend, resolveCurrencyCode(item.currency))}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                      {formatJoinedDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/customers/${item.id}`} className="text-primary hover:underline">
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <LoadMoreFooter
            shown={customers.length}
            total={listTotal}
            hasMore={hasMore}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </>
      )}
    </Card>
  )
}
